/**
 * ModuleToolbarContext
 * Story 6.1: Implement Unified Toolbar Wrapper
 *
 * Provides toolbar state and actions to child components.
 * Allows child components (like SheetView) to register undo/redo handlers.
 */
import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

export type ToolbarTabType = 'grid' | 'dashboard' | 'applet';

interface UndoRedoHandlers {
  onUndo: () => void;
  onRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}

interface ModuleToolbarContextValue {
  /** Call to perform undo on active pane */
  onUndo: () => void;
  /** Call to perform redo on active pane */
  onRedo: () => void;
  /** Whether undo is available */
  canUndo: boolean;
  /** Whether redo is available */
  canRedo: boolean;
  /** Current active tab type */
  activeTabType: ToolbarTabType;
  /** Register undo/redo handlers from child components */
  registerUndoRedo: (handlers: UndoRedoHandlers) => void;
  /** Unregister undo/redo handlers */
  unregisterUndoRedo: () => void;
}

const ModuleToolbarContext = createContext<ModuleToolbarContextValue | null>(null);

/**
 * Hook to access toolbar context
 * Returns no-op defaults when used outside provider for graceful degradation
 */
export const useModuleToolbar = (): ModuleToolbarContextValue => {
  const context = useContext(ModuleToolbarContext);
  if (!context) {
    return {
      onUndo: () => {},
      onRedo: () => {},
      canUndo: false,
      canRedo: false,
      activeTabType: 'grid',
      registerUndoRedo: () => {},
      unregisterUndoRedo: () => {},
    };
  }
  return context;
};

interface ModuleToolbarProviderProps {
  children: ReactNode;
  activeTabType?: ToolbarTabType;
}

/**
 * Provider for module toolbar context
 * Manages undo/redo state and delegates to registered child handlers
 */
export const ModuleToolbarProvider = ({
  children,
  activeTabType = 'grid',
}: ModuleToolbarProviderProps) => {
  const [handlers, setHandlers] = useState<UndoRedoHandlers | null>(null);

  const registerUndoRedo = useCallback((newHandlers: UndoRedoHandlers) => {
    setHandlers(newHandlers);
  }, []);

  const unregisterUndoRedo = useCallback(() => {
    setHandlers(null);
  }, []);

  const onUndo = useCallback(() => {
    handlers?.onUndo();
  }, [handlers]);

  const onRedo = useCallback(() => {
    handlers?.onRedo();
  }, [handlers]);

  const value: ModuleToolbarContextValue = {
    onUndo,
    onRedo,
    canUndo: handlers?.canUndo ?? false,
    canRedo: handlers?.canRedo ?? false,
    activeTabType,
    registerUndoRedo,
    unregisterUndoRedo,
  };

  return (
    <ModuleToolbarContext.Provider value={value}>
      {children}
    </ModuleToolbarContext.Provider>
  );
};

export { ModuleToolbarContext };
